import { useEffect, useState } from 'react';
import { confirmDialog } from 'primereact/confirmdialog';
import { toast } from 'sonner';
import type { VoxServiceCardResponse } from '@/core/generated/api';
import { voxServiceService } from '@/entities/VoxService/api/voxServiceService';
import { VOX_SERVICE_COLUMNS } from '@/entities/VoxService/config/vox-service-table.config';
import { getAvailableVoxServiceProducts } from '@/entities/VoxService/model/availableProducts';
import { useVoxServices } from '@/entities/VoxService/model/store';
import { VoxServiceEditor } from '@/features/VoxServices/VoxServiceEditor';
import { getErrorMessage } from '@/shared/lib/errors';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { RequestStatus } from '@/shared/model/requestStatus';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import type { ActionMenuItem } from '@/shared/ui/ActionMenu/types';
import { DataTable } from '@/shared/ui/DataTable/DataTable';
import { RequestEmpty, RequestError, RequestErrorBanner } from '@/shared/ui/RequestState';
import classes from './VoxServicesPage.module.scss';

/** Управляет карточками услуг приложения */
const VoxServicesPage = () => {
    const catalog = useVoxServices(state => state.catalog);
    const status = useVoxServices(state => state.status);
    const error = useVoxServices(state => state.error);
    const load = useVoxServices(state => state.load);
    const reset = useVoxServices(state => state.reset);
    const canView = useCanAccessPermission('admin.vox-services.view');
    const canCreate = useCanAccessPermission('admin.vox-services.create');
    const canEdit = useCanAccessPermission('admin.vox-services.edit');
    const canDelete = useCanAccessPermission('admin.vox-services.delete');
    const [editing, setEditing] = useState<VoxServiceCardResponse | null | undefined>();
    const [deleting, setDeleting] = useState(false);
    const [actionError, setActionError] = useState<string | null>(null);
    const cards = catalog?.items ?? [];
    const products = catalog?.products ?? [];
    const loading = status === RequestStatus.Loading;
    const available = getAvailableVoxServiceProducts(products, cards).length;

    useEffect(() => {
        if (canView) void load();

        return reset;
    }, [canView, load, reset]);

    /** Подтверждает удаление карточки услуги */
    const remove = (card: VoxServiceCardResponse): void => {
        if (!canDelete || deleting || !card.id || !card.version) return;
        const { id, version } = card;

        confirmDialog({
            header: 'Удалить карточку услуги?',
            message: `Карточка «${card.name}» исчезнет из приложения. Сам продукт останется.`,
            acceptLabel: 'Удалить',
            rejectLabel: 'Отмена',
            defaultFocus: 'reject',
            acceptClassName: 'p-button-danger',
            accept: async () => {
                setDeleting(true);
                setActionError(null);

                try {
                    await voxServiceService.remove(id, version);
                    toast.success('Карточка услуги удалена');
                    await load();
                } catch (failure: unknown) {
                    setActionError(getErrorMessage(failure, 'Не удалось удалить карточку'));
                } finally {
                    setDeleting(false);
                }
            },
        });
    };

    /** Предлагает доступные действия карточки */
    const actions = (card: VoxServiceCardResponse): ActionMenuItem[] => [
        {
            label: 'Редактировать',
            icon: 'pi pi-pencil',
            permission: 'admin.vox-services.edit',
            disabled: loading || deleting,
            command: () => setEditing(card),
        },
        {
            label: 'Удалить',
            icon: 'pi pi-trash',
            permission: 'admin.vox-services.delete',
            disabled: loading || deleting,
            command: () => remove(card),
        },
    ];

    if (!canView) return <RequestEmpty message="Нет доступа к карточкам услуг" />;

    return <div className={classes.page}>
        <div className={classes.heading}>
            <div>
                <h1>Услуги VOX</h1>
                <p>Карточки на экране выбора услуги в приложении. Один продукт — одна карточка.</p>
            </div>
            {canCreate &&
                <ActionButton
                    label="Новая карточка"
                    icon="pi pi-plus"
                    disabled={loading || deleting || !catalog || available === 0}
                    onClick={() => setEditing(null)}
                />
            }
        </div>
        <RequestErrorBanner
            status={status}
            error={error}
            fallbackMessage="Не удалось загрузить карточки услуг"
            onRetry={load}
        />
        {actionError &&
            <RequestError
                compact
                message={actionError}
            />
        }
        {catalog &&
            <p className={classes.summary}>Карточек: {cards.length} · В приложении: {cards.filter(card => card.isShowServices).length} · Продуктов без карточки: {available}</p>
        }
        <DataTable
            title="Карточки услуг"
            filterDisplay="menu"
            data={cards}
            columns={VOX_SERVICE_COLUMNS}
            loading={loading}
            totalRecords={cards.length}
            paginator
            rowActions={actions}
            onRowClick={canEdit && !deleting
                ? card => setEditing(card)
                : undefined
            }
            emptyMessage={status === RequestStatus.Error
                ? 'Не удалось загрузить карточки.'
                : 'Карточек услуг пока нет.'
            }
        />
        {editing !== undefined &&
            <VoxServiceEditor
                card={editing ?? undefined}
                cards={cards}
                products={products}
                onClose={() => setEditing(undefined)}
                onSaved={() => {
                    setEditing(undefined);
                    void load();
                }}
            />
        }
    </div>;
};

export default VoxServicesPage;
